import { useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router'
import { AppShell } from '../../app/AppShell'
import { api } from '../../api/client'
import { campaignKeys, useCampaignWorld } from '../../domains/campaigns/queries'
import { galleryKeys } from '../../domains/images/queries'
import { ImageGallery } from '../../domains/images/ImageGallery'
import { GenerationCard } from '../../domains/jobs/GenerationCard'
import { useGenerationJob } from '../../domains/jobs/useGenerationJob'
import type { GenerationJob } from '../../domains/jobs/types'
import { Button, Card, SectionLabel } from '../../design/components'
import styles from '../play/play.module.css'

/**
 * A place's gallery, the two images the generator can draw for it, and the way through to the
 * workbench.
 *
 * Both generations are Game Master-only, like every other write under a city — and the
 * gallery's own `canManage` says so, so the cards hang off that rather than off a rule
 * re-derived here.
 *
 * **A button and a workbench, deliberately both.** The Generate cards read the location's name
 * and description and send them; the composer hands over the whole prompt, including reference
 * pictures and the model's knobs. That is the same pairing the character gallery has, for the
 * same reason: most of the time you want a picture and have no opinions, and the rest of the
 * time nothing else will do.
 */
export function LocationImagesPage() {
  const params = useParams<{ id: string; locationId: string }>()
  const campaignId = Number(params.id)
  const locationId = Number(params.locationId)

  const queryClient = useQueryClient()
  const world = useCampaignWorld(campaignId)

  const owner = { kind: 'location' as const, campaignId, locationId }
  const location = (world.data?.sections.locations?.locations ?? []).find(
    (candidate) => candidate.id === locationId,
  )
  const isGameMaster = world.data?.isGameMaster === true

  // The world sheet carries each location's `imageUrl` and `battleMapImageUrl`, so it is
  // refetched alongside the gallery: the city page's thumbnail is the visible proof.
  const refetchOwner = () => {
    void queryClient.invalidateQueries({ queryKey: galleryKeys.gallery(owner) })
    void queryClient.invalidateQueries({ queryKey: campaignKeys.world(campaignId) })
  }

  const visualization = useGenerationJob({
    start: () =>
      api.post<GenerationJob>(
        `/campaigns/${campaignId}/locations/${locationId}/visualization/generate`,
        { descriptionOverride: null },
      ),
    onSettled: refetchOwner,
  })

  const battleMap = useGenerationJob({
    start: () =>
      api.post<GenerationJob>(
        `/campaigns/${campaignId}/locations/${locationId}/battle-map/generate`,
        { descriptionOverride: null },
      ),
    onSettled: refetchOwner,
  })

  if (Number.isNaN(campaignId) || Number.isNaN(locationId)) {
    return (
      <AppShell title="Images">
        <Card>
          <p className={styles.note}>That is not a place.</p>
        </Card>
      </AppShell>
    )
  }

  const composePath = `/campaigns/${campaignId}/locations/${locationId}/compose`

  return (
    <AppShell title={location?.name ?? 'Place'} subtitle="GALLERY">
      <ImageGallery owner={owner} label="Gallery" />

      {isGameMaster && (
        <Card variant="magic" label="Compose">
          <p className={styles.note}>
            The workbench: build the prompt yourself out of text and reference pictures, turn the
            model&apos;s knobs, and keep only the results worth keeping.
          </p>

          <SectionLabel tone="muted" micro>
            Draw
          </SectionLabel>

          <Link to={`${composePath}?type=Depiction`}>
            <Button variant="secondary" fullWidth>
              Compose a depiction
            </Button>
          </Link>

          <Link to={`${composePath}?type=BattleMap`}>
            <Button variant="secondary" fullWidth>
              Compose a battle map
            </Button>
          </Link>
        </Card>
      )}

      <GenerationCard
        label="Draw the place"
        what="The location's name and description drive the prompt. It runs in the background; the gallery refreshes when it lands."
        action="Generate an image"
        canGenerate={isGameMaster}
        job={visualization}
      />

      <GenerationCard
        label="Draw a battle map"
        what="The same description, rendered as a top-down map for a fight. A place holds one map — this replaces any it already has."
        action="Generate a battle map"
        canGenerate={isGameMaster}
        job={battleMap}
      />

      <Link to={`/campaigns/${campaignId}/city`} className={styles.backLink}>
        <Button variant="secondary" fullWidth>
          Back to the city
        </Button>
      </Link>
    </AppShell>
  )
}
